export type ParsedPurchaseOrderDateTime = {
  kind: "date" | "timestamp";
  date: Date;
};

const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Parse hub purchase-order date and timestamp strings.
 * Postgres `timestamptz::text` looks like `2026-08-04 20:58:31.517091-05` (space, no `T`).
 * A calendar date with no time stays a date; do not invent noon for display.
 */
export function parsePurchaseOrderDateTime(
  value: string | null | undefined,
): ParsedPurchaseOrderDateTime | null {
  if (value == null) {
    return null;
  }
  const trimmed = value.trim();
  if (trimmed === "") {
    return null;
  }
  if (DATE_ONLY_RE.test(trimmed)) {
    const parsed = new Date(`${trimmed}T12:00:00`);
    if (Number.isNaN(parsed.getTime())) {
      return null;
    }
    return { kind: "date", date: parsed };
  }
  let normalized = trimmed.includes("T") ? trimmed : trimmed.replace(" ", "T");
  normalized = normalized.replace(/([+-]\d{2})$/, "$1:00");
  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return { kind: "timestamp", date: parsed };
}

/** Calendar date only (purchase order list). */
export function formatPurchaseOrderListDate(iso: string | null | undefined): string {
  const parsed = parsePurchaseOrderDateTime(iso);
  if (parsed == null) {
    const trimmed = iso?.trim() ?? "";
    return trimmed === "" ? "—" : trimmed.slice(0, 10);
  }
  return parsed.date.toLocaleDateString();
}

/** Instant when the value has a time; calendar date when it is date-only. */
export function formatPurchaseOrderTimestamp(iso: string | null | undefined): string {
  const parsed = parsePurchaseOrderDateTime(iso);
  if (parsed == null) {
    const trimmed = iso?.trim() ?? "";
    return trimmed === "" ? "—" : trimmed;
  }
  if (parsed.kind === "date") {
    return parsed.date.toLocaleDateString();
  }
  return parsed.date.toLocaleString();
}
